import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, Injectable } from '@angular/core';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SessionService } from '@monsido/core/session/session.service';
import {
    DefaultConstraintsAndExcludesService,
} from '@client/app/forms/domain/default-constraints-and-excludes-service/default-constraints-and-excludes.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { DemoHasAccessService } from '@client/app/modules/demodata/services/demo-has-access.service';
import moment from 'moment';
import { ControlContainer, NgForm } from '@angular/forms';
import { NgbTimeAdapter, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';

@Injectable()
export class NgbTimeDateAdapter extends NgbTimeAdapter<string> {
    fromModel (value: string | null): NgbTimeStruct | null {
        if (!value) {
            return null;
        }
        const m = moment(value);
        return {
            hour: m.hour(),
            minute: m.minute(),
            second: m.second(),
        };
    }

    toModel (time: NgbTimeStruct | null): string | null {
        if (!time) {
            return null;
        }
        const current = moment();
        current.set('hour', time.hour);
        current.set('minute', time.minute);
        current.set('second', time.second);
        return `${time.hour}:${time.minute}:${time.second}`;
    }
}

@Component({
    selector: 'form-domain-scan',
    templateUrl: './form-domain-scan.component.html',
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    standalone: false,
    providers: [
        { provide: NgbTimeAdapter, useClass: NgbTimeDateAdapter },
    ],
})
export class FormDomainScanComponent implements OnInit, OnDestroy {
    @Input() domain!: any;
    @Output() basicAuthHostValidityChange = new EventEmitter<boolean>();

    // Component state
    customer: any;
    hasCustomScanTrait = false;
    hasCrawlAutomatically = false;
    isContstraintsLoading = false;
    isLinkExcludesLoading = false;
    constraintsRightItemLabels: any[] = [];
    linkExcludesRightItemLabels: any[] = [];
    labelTooltipText = '';
    dateTimeFormat = 'lll';
    nextScanDate = '';
    scanIntervalValue = 0;
    initialScanIntervalValue = 0;
    scanIntervalName = '';

    // Options
    dayOptions: Array<{ name: string; value: string }> = [];
    scanIntervalOptions: Array<{ name: string; value: number }> = [];
    connectionOptions: Array<{ name: string; value: number }> = [];
    loginTypes: Array<{ name: string; value: string }> = [];

    // Constants
    private readonly defaultText: string;
    private readonly defaultTagValue = 'default';
    private readonly labelDeleteConfirmation: string;

    // Subscriptions
    private pathConstraintsSubscription?: Subscription;
    private linkExcludesSubscription?: Subscription;
    private loadingPathConstraitsProgressSubscription?: Subscription;
    private loadingLinksExcludedProgressSubscription?: Subscription;

    constructor (
        private translateService: TranslateService,
        private sessionService: SessionService,
        private defaultConstraintsAndExcludesService: DefaultConstraintsAndExcludesService,
        private monModalService: MonModalService,
        private activeFeatureService: ActiveFeatureService,
        private demoHasAccessService: DemoHasAccessService,
    ) {
        this.defaultText = this.translateService.getString('default');
        this.labelDeleteConfirmation = this.translateService.getString('Are you sure you want to remove the Default label?');
        this.labelTooltipText = this.translateService.getString(`Most users of your selected CMS apply this pattern when configuring their domain in Acquia Web Governance.
        Edit the pattern or remove the Default label to permanently save it for your domain.`);
    }

    ngOnInit (): void {
        this.customer = this.sessionService.customer;
        this.hasCustomScanTrait = this.activeFeatureService.isFeatureActivePure('custom_scan');
        this.hasCrawlAutomatically = this.activeFeatureService.hasAccountAccessToFeature('crawl_automatically');

        this.setupDayOptions();
        this.setupConnectionOptions();
        this.setupLoginOptions();
        this.setupIntervalOptions();
        this.updateScanInterval();
        this.updateNextScanDate();

        this.setupSubscriptions();
        this.updatePathConstraints([]);
        this.updateLinkExcludes([]);

        // uib-timepicker interacts with date object
        this.domain.scan.time = new Date(this.domain.scan.time);

        if (!this.domain.additional_internal_urls) {
            this.domain.additional_internal_urls = [];
        }
    }

    ngOnDestroy (): void {
        this.pathConstraintsSubscription?.unsubscribe();
        this.linkExcludesSubscription?.unsubscribe();
        this.loadingPathConstraitsProgressSubscription?.unsubscribe();
        this.loadingLinksExcludedProgressSubscription?.unsubscribe();
    }

    private setupSubscriptions (): void {
        this.loadingPathConstraitsProgressSubscription = this.defaultConstraintsAndExcludesService.loadingPathConstraitsProgress$.subscribe((value: boolean) => {
            this.isContstraintsLoading = value;
        });

        this.loadingLinksExcludedProgressSubscription = this.defaultConstraintsAndExcludesService.loadingLinksExcludedProgress$.subscribe((value: boolean) => {
            this.isLinkExcludesLoading = value;
        });

        // We have to init without default data
        this.pathConstraintsSubscription = this.defaultConstraintsAndExcludesService.defaultPathConstraints$.pipe(skip(1)).subscribe((data: any[]) => {
            // We need to wait until domain.cms is updated
            setTimeout(() => {
                this.updatePathConstraints(data);
            });
        });

        this.linkExcludesSubscription = this.defaultConstraintsAndExcludesService.defaultLinkExcludes$.pipe(skip(1)).subscribe((data: any[]) => {
            // We need to wait until domain.cms is updated
            setTimeout(() => {
                this.updateLinkExcludes(data);
            });
        });
    }

    updateNextScanDate (): void {
        if (!this.hasCustomScanTrait && this.scanIntervalValue !== -1) {
            this.nextScanDate = this.getNextScan(this.domain, this.scanIntervalValue).format(this.dateTimeFormat);
        }
    }

    setScanInterval (): void {
        if (this.initialScanIntervalValue !== this.scanIntervalValue) {
            this.domain.scan.scan_interval = this.scanIntervalValue;
            this.initialScanIntervalValue = this.scanIntervalValue;
        }
    }

    private updateScanInterval (): void {
        let interval = this.customer.plan_traits.scan_interval;
        if (this.hasCustomScanTrait && this.domain.scan.scan_interval) {
            interval = this.domain.scan.scan_interval;
        }
        const scanInterval = this.scanIntervalOptions.find((option) => (option.value === interval));
        const scanIntervalValue = scanInterval?.value || interval;

        this.initialScanIntervalValue = this.scanIntervalValue = scanIntervalValue;
        this.scanIntervalName = scanInterval?.name || this.translateService.getString('Custom');
    }

    private setupLoginOptions (): void {
        this.loginTypes = [
            {
                name: this.translateService.getString('None'),
                value: 'none',
            },
            {
                name: this.translateService.getString('Form'),
                value: 'form',
            },
            {
                name: this.translateService.getString('Office 365 (BETA)'),
                value: 'office365',
            },
            {
                name: this.translateService.getString('Basic Auth'),
                value: 'basic_auth',
            },
            {
                name: this.translateService.getString('Custom Multistep'),
                value: 'custom_multistep',
            },
        ];
    }

    private setupDayOptions (): void {
        this.dayOptions = [
            {
                name: this.translateService.getString('Autoselect'),
                value: 'any',
            },
            {
                name: this.translateService.getString('Sunday'),
                value: 'sunday',
            },
            {
                name: this.translateService.getString('Monday'),
                value: 'monday',
            },
            {
                name: this.translateService.getString('Tuesday'),
                value: 'tuesday',
            },
            {
                name: this.translateService.getString('Wednesday'),
                value: 'wednesday',
            },
            {
                name: this.translateService.getString('Thursday'),
                value: 'thursday',
            },
            {
                name: this.translateService.getString('Friday'),
                value: 'friday',
            },
            {
                name: this.translateService.getString('Saturday'),
                value: 'saturday',
            },
        ];
    }

    private setupIntervalOptions (): void {
        this.scanIntervalOptions = [
            {
                name: this.translateService.getString('Weekly'),
                value: 7,
            },
            {
                name: this.translateService.getString('Every 2 weeks'),
                value: 14,
            },
            {
                name: this.translateService.getString('Every 4 weeks'),
                value: 28,
            },
            {
                name: this.translateService.getString('Every 8 weeks'),
                value: 56,
            },
            {
                name: this.translateService.getString('Every 12 weeks'),
                value: 84,
            },
        ];
        if (this.demoHasAccessService.isDemoAccount()) {
            this.scanIntervalOptions.push({
                name: this.translateService.getString('Never'),
                value: -1,
            });
        }
    }

    private setupConnectionOptions (): void {
        this.connectionOptions = [
            {
                name: this.translateService.getString('Slow'),
                value: 30,
            },
            {
                name: this.translateService.getString('Normal - recommended'),
                value: 60,
            },
            {
                name: this.translateService.getString('Faster'),
                value: 90,
            },
            {
                name: this.translateService.getString('Very fast - Be Cautious'),
                value: 120,
            },
            {
                name: this.translateService.getString('Superfast - Don\'t use unless you know what you\'re doing'),
                value: 180,
            },
        ];
    }

    onDeleteConstraintsDefaultLabelClick (index: number): void {
        const entry = this.domain.path_constraints[index];
        this.monModalService
            .confirm(this.labelDeleteConfirmation)
            .then(() => {
                if (this.domain.path_constraints.includes(entry) && Array.isArray(entry.tags)) {
                    entry.tags.splice(0, 2);
                    this.domain.path_constraints = Array.from(this.domain.path_constraints);
                    this.constraintsRightItemLabels = this.makeRightDefaultLabels(this.domain.path_constraints);
                }
            }, () => {});
    }

    onDeleteLinksDefaultLabelClick (index: number): void {
        const entry = this.domain.link_excludes[index];
        this.monModalService
            .confirm(this.labelDeleteConfirmation)
            .then(() => {
                if (this.domain.link_excludes.includes(entry) && Array.isArray(entry.tags)) {
                    entry.tags.splice(0, 2);
                    this.domain.link_excludes = Array.from(this.domain.link_excludes);
                    this.linkExcludesRightItemLabels = this.makeRightDefaultLabels(this.domain.link_excludes);
                }
            }, () => {});
    }

    onInputValueChanged (changes: any, type: 'pathConstraints' | 'linkExcludes'): void {
        const list = type === 'pathConstraints' ? this.domain.path_constraints : this.domain.link_excludes;
        const entry = list[changes.index];
        if (Array.isArray(entry.tags) && entry.tags[0] === this.defaultTagValue) {
            entry.tags.splice(0, 2);
            if (type === 'pathConstraints') {
                this.domain.path_constraints = Array.from(list);
                this.constraintsRightItemLabels = this.makeRightDefaultLabels(this.domain.path_constraints);
            } else {
                this.domain.link_excludes = Array.from(list);
                this.linkExcludesRightItemLabels = this.makeRightDefaultLabels(this.domain.link_excludes);
            }
        }
    }

    onBasicAuthHostValidityChange (valid: boolean): void {
        this.basicAuthHostValidityChange.emit(valid);
    }

    private removeUnrelatedEntries (data: any[]): any[] {
        return data.filter(entry => {
            if (!entry) {
                return false;
            }
            if (!Array.isArray(entry.tags)) {
                return true;
            }
            if (entry.tags[0] === this.defaultTagValue && entry.tags[1] !== this.domain.cms) {
                return false;
            }
            return true;
        });
    }

    private removeNewerDuplicates (data: any[], keyName: string): any[] {
        const safeData = data.filter(entry => !!entry);

        return safeData
            .map(entry => {
                return entry[keyName];
            })
            .reduce((acc, current, index, entries) => {
                if (index === entries.lastIndexOf(current)) {
                    acc.push(safeData[index]);
                }
                return acc;
            }, []);
    }

    private makeRightDefaultLabels (items: any[]): any[] {
        return items.map(entry => {
            if (entry.tags && entry.tags[0] === this.defaultTagValue) {
                return { name: this.defaultText };
            }
            return undefined;
        });
    }

    private updatePathConstraints (data: any[]): void {
        if (this.domain && this.domain.path_constraints) {
            const pathConstraints = this.removeUnrelatedEntries(this.domain.path_constraints);
            this.domain.path_constraints = this.removeNewerDuplicates(data.concat(pathConstraints), 'constraint');
            this.constraintsRightItemLabels = this.makeRightDefaultLabels(this.domain.path_constraints);
        }
    }

    private updateLinkExcludes (data: any[]): void {
        if (this.domain && this.domain.link_excludes) {
            const linkExcludes = this.removeUnrelatedEntries(this.domain.link_excludes);
            this.domain.link_excludes = this.removeNewerDuplicates(data.concat(linkExcludes), 'regex');
            this.linkExcludesRightItemLabels = this.makeRightDefaultLabels(this.domain.link_excludes);
        }
    }

    private getNextScan (domain: any, scanInterval: number): any {
        const current = moment(domain.last_scan || undefined);
        let weekday = current.day();
        if (domain.scan.day !== 'any') {
            weekday = moment(domain.scan.time).day(domain.scan.day)
                .utcOffset(0)
                .isoWeekday();
        }
        const days = (scanInterval + weekday - current.day());
        return current.clone().add(days, 'd')
            .set('hour', moment(domain.scan.time).get('hour'))
            .set('minute', moment(domain.scan.time).get('minute'));
    }
}
